import * as React from 'react';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import { Row, Button, InputGroup } from 'reactstrap';
import { MdCompareArrows, MdHighlightOff } from 'react-icons/md';
import classNames from 'classnames';
import { ChartOptions, ChartTooltipLabelColor, ChartYAxe } from 'chart.js';
import ChartUtils, { chartCustomFnFactory, chartHoverFnFactory } from '../utils/ChartUtils';
import DetailedChartLegend from '../charts/DetailedChartLegend';
import { Duration } from '../models/enums/Duration';
import DetailedChartKpiValue from '../charts/DetailedChartKpiValue';
import LoadingChart from '../../overview/assets/loading_chart.svg';
import { defaultDuration } from '../models/enums/ComponentOptions';
import ValueExpression from '../../valueexpressions/models/valueexpressions/ValueExpression';
import KpiSelector from './KpiSelector';
import DetailedChartTooltip from '../charts/DetailedChartTooltip';
import DateSelect from '../../common/components/DateSelect';
import { formatValueExpressionValue } from '../utils/FormatValueExpression';
import { Position } from '../types/Position';
import { dashboardSlice, selectDashboardOrgKey, selectLocalComponentOptions } from '@redux';
import { EmptyKpiSelectionInfo } from './EmptyKpiSelectionInfo';
import valueExpressionMap from '@contexts/value-expression-context/value-expressions';
import type { TooltipModel } from '../charts/DetailedChartTooltip';
import * as Loader from '../cssModules/loaderStyles.module.scss';
import Styles from './KpiCompareChart.module.scss';
import { useChartKpiQuery } from '@hooks/use-chart-kpi-query-hook';
import { useKpiQuery } from '@hooks/use-kpi-query-hook';
import { DurationUtils } from '@utils/duration-utils';
import { DatasetType } from '../charts/DetailedChart';

type Props = {
  uID: string | null | undefined;
  showDateSelector: boolean;
  style?: React.CSSProperties;
};

const PRIMARY_AXIS_ID = 'A';
const COMPARE_AXIS_ID = 'B';

const KpiCompareChart: React.FC<Props> = (props: Props) => {
  const { showDateSelector, style, uID } = props;

  const dispatch = useDispatch();
  const orgKey = useSelector(selectDashboardOrgKey);
  const options = useSelector(selectLocalComponentOptions(uID, 'kennzahlenVergleich'));
  const tableOptions = useSelector(selectLocalComponentOptions(uID, 'kennzahlenTable'));

  const firstValueExpressionKey = options?.primaryValueExpressionKey;
  const firstValueExpression = valueExpressionMap.get(firstValueExpressionKey);
  const secondValueExpressionKey = options?.compareValueExpressionKey;
  const secondValueExpression = valueExpressionMap.get(secondValueExpressionKey);
  const duration = options?.duration || defaultDuration(uID);

  const [tooltipModel, setTooltipModel] = useState<TooltipModel>({} as TooltipModel);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number>(-1);
  const [tooltipPosition, setTooltipPosition] = useState<Position>({ x: 0, y: 0 });

  const chartRef = useRef<Bar>(null);

  const setComponentsOptions = (componentId: string, optionKey: string, value: unknown) => {
    dispatch(
      dashboardSlice.actions.setLocalComponentOptions({
        componentId,
        optionKey,
        value,
      })
    );
  };

  const { data: firstKpiPrimaryData, loading: firstKpiPrimaryDataLoading } = useChartKpiQuery({
    orgKey,
    duration,
    valueExpression: firstValueExpression,
  });

  const { data: firstKpiCompareValue, isLoading: firstKpiCompareValueLoading } = useKpiQuery(
    {
      expressionsFilter: [firstValueExpression].map((ve) => {
        return { valueExpression: ve };
      }),
      orgKeys: [orgKey],
      dateRange: DurationUtils.getComparisonDateRange(duration),
    },
    {
      enabled: !!firstValueExpression,
    },
    (data) => data?.kpis?.summary?.kpiValues?.[0]?.value
  );

  const { data: secondKpiPrimaryData, loading: secondKpiPrimaryDataLoading } = useChartKpiQuery({
    orgKey,
    duration,
    valueExpression: secondValueExpression,
  });

  const { data: secondKpiCompareValue, isLoading: secondKpiCompareValueLoading } = useKpiQuery(
    {
      expressionsFilter: [secondValueExpression].map((ve) => {
        return { valueExpression: ve };
      }),
      orgKeys: [orgKey],
      dateRange: DurationUtils.getComparisonDateRange(duration),
    },
    {
      enabled: !!secondValueExpression,
    },
    (data) => data?.kpis?.summary?.kpiValues?.[0]?.value
  );

  const isLoading =
    firstKpiPrimaryDataLoading ||
    firstKpiCompareValueLoading ||
    secondKpiPrimaryDataLoading ||
    secondKpiCompareValueLoading;

  const _getChartOptions = (onActiveTooltipIndex: (idx: number) => void): ChartOptions => {
    const yAxes: ChartYAxe[] = [
      {
        id: PRIMARY_AXIS_ID,
        type: 'linear',
        position: 'left',
        stacked: false,
        gridLines: {
          color: 'rgba(0,0,0,0.04)',
        },
        ticks: {
          fontColor: 'rgba(0, 145, 205, 1)',
          beginAtZero: [firstValueExpression, secondValueExpression]
            .filter((x) => x != null)
            .some((x) => x.chartRenderOptions?.startYAxisOnZero !== false),
          suggestedMax: firstValueExpression.percent ? null : 10,
          step: 1,
          callback: (value) => formatValueExpressionValue(firstValueExpression, value),
        },
      },
    ];

    if (secondValueExpressionKey && secondValueExpression) {
      yAxes.push({
        id: COMPARE_AXIS_ID,
        type: 'linear',
        position: 'right',
        stacked: false,
        gridLines: false,
        ticks: {
          suggestedMax: secondValueExpression.percent ? null : 10,
          beginAtZero: true,
          callback: (value) => formatValueExpressionValue(secondValueExpression, value),
        },
      });
    }

    return {
      legend: {
        display: false,
      },
      layout: {
        padding: {
          left: 20,
          bottom: 20,
        },
      },
      hover: {
        mode: 'nearest',
      },
      animation: { duration: 0 },
      maintainAspectRatio: false,
      scales: {
        yAxes,
        xAxes: [
          {
            stacked: false,
            gridLines: {
              color: 'rgba(0,0,0,0.04)',
            },
            ticks: {
              autoSkip: true,
              autoSkipPadding: 10,
              maxRotation: 0,
              fontColor: 'hsl(203, 100%, 7%)',
            },
          },
        ],
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        position: 'nearest',
        enabled: false,
        callbacks: {
          title: (tooltipItem, data) => {
            const index = tooltipItem?.[0]?.index;
            return data?.dataLabels?.[index];
          },
          beforeLabel: (tooltipItem) => {
            return `${tooltipItem?.datasetIndex || 0}`;
          },
          afterLabel: (tooltipItem, data) => {
            if (data?.datasets?.[tooltipItem?.datasetIndex]?.yAxisID === PRIMARY_AXIS_ID) {
              return `${formatValueExpressionValue(firstValueExpression, tooltipItem?.yLabel)}`;
            } else {
              return `${formatValueExpressionValue(secondValueExpression, tooltipItem?.yLabel)}`;
            }
          },
          labelColor: (tooltipItem, chart) => {
            return chart?.config?.data?.datasets?.[tooltipItem?.datasetIndex] as ChartTooltipLabelColor;
          },
          label: (tooltipItem, data) => {
            const dataset = data?.datasets?.[tooltipItem?.datasetIndex];
            return dataset?.label;
          },
        },
        custom: chartCustomFnFactory(
          (tooltipModel) => {
            setTooltipModel(tooltipModel);
          },
          (p) => setTooltipPosition(p)
        ),
      },
      onHover: chartHoverFnFactory(onActiveTooltipIndex, (p) => setTooltipPosition(p)),
    };
  };

  const _getChartData = () => {
    const { from, to } = ChartUtils.getDateRange(duration);
    const dateRanges = ChartUtils.getDateRanges(from, to);
    const comparisonRange = ChartUtils.getComparisonDateRange(duration);
    const compareRanges = ChartUtils.getDateRanges(comparisonRange.from, comparisonRange.to);

    if (!firstValueExpression || isLoading) {
      return;
    }

    const firstKpiPrimaryRangeData = firstKpiPrimaryData?.rangeData;
    const secondKpiPrimaryRangeData = secondKpiPrimaryData?.rangeData;

    const datasets = (() => {
      const mapDataFn = (isComparisonData?: boolean) => {
        return dateRanges.dates.map((dt) => {
          if (isComparisonData) {
            const compareTypeKey = secondValueExpression.identifier;
            const compareDateKey = `${dt.format('YYYY-MM-DD')}_${ChartUtils.translateMomentRangeToGrouping(
              dateRanges.resolution
            )}_${compareTypeKey}`;
            return secondKpiPrimaryRangeData?.[compareDateKey] ?? null;
          } else {
            const firstTypeKey = firstValueExpression.identifier;
            const firstDateKey = `${dt.format('YYYY-MM-DD')}_${ChartUtils.translateMomentRangeToGrouping(
              dateRanges.resolution
            )}_${firstTypeKey}`;
            return firstKpiPrimaryRangeData?.[firstDateKey] ?? null;
          }
        });
      };

      const firstKpiPrimaryDatasets = [
        {
          yAxisID: PRIMARY_AXIS_ID,
          primary: true,
          label: firstValueExpression.getLabel(),
          type: 'line',
          fill: false,
          lineTension: 0,
          pointRadius: 3,
          pointBorderWidth: 2,
          pointBackgroundColor: '#FFFFFF',
          spanGaps: true,
          borderColor: '#0091cd',
          backgroundColor: 'rgba(0, 145, 205, 1)',
          data: mapDataFn(),
        },
      ];

      if (!secondValueExpression || !secondKpiPrimaryRangeData) {
        return firstKpiPrimaryDatasets;
      }

      const compareDatasets = [
        {
          yAxisID: COMPARE_AXIS_ID,
          primary: true,
          type: 'bar',
          fill: false,
          label: secondValueExpression.getLabel(),
          lineTension: 0,
          pointRadius: 3,
          pointBorderWidth: 2,
          pointBackgroundColor: '#FFFFFF',
          spanGaps: true,
          borderColor: '#b2b5ba',
          backgroundColor: 'rgba(178, 181, 186, 1)',
          data: mapDataFn(true),
        },
      ];
      return [...compareDatasets, ...firstKpiPrimaryDatasets].reverse();
    })();

    return {
      labels: dateRanges.axisLabels,
      dataLabels: dateRanges.dataLabels,
      compareDataLabels: compareRanges.dataLabels,
      labelValues: dateRanges.dates,
      datasets,
    };
  };

  const _onLegendItemClick = (legendItem) => {
    const ci = chartRef?.current?.chartInstance;
    const meta = ci.getDatasetMeta(legendItem.legendIndex);
    meta.hidden = meta.hidden === null ? !ci.data.datasets[legendItem.legendIndex].hidden : null;
    ci.update();
  };

  const _onLegendItemToggleAll = (datasets, hide: boolean) => {
    const ci = chartRef?.current?.chartInstance;
    datasets.forEach((ds, legendIndex) => {
      const meta = ci.getDatasetMeta(legendIndex);
      meta.hidden = hide;
    });
    ci.update();
  };

  const _onPrimaryValueExpressionSelect = (veKey: string) => {
    componentOptionsToState({ primaryValueExpressionKey: veKey });
  };

  const _onCompareValueExpressionSelect = (veKey: string) => {
    componentOptionsToState({ compareValueExpressionKey: veKey });
  };

  const _swap = () => {
    componentOptionsToState({
      primaryValueExpressionKey: secondValueExpressionKey,
      compareValueExpressionKey: firstValueExpressionKey,
    });
  };

  const _removeCompare = () => {
    componentOptionsToState({ compareValueExpressionKey: '' });
  };

  const _onDurationSelected = (duration: Duration): void => {
    componentOptionsToState({ duration });
  };

  const componentOptionsToState = (update) => {
    if (!uID) {
      return;
    }

    const newOptions = { ...options, ...update };
    setComponentsOptions(uID, 'kennzahlenVergleich', newOptions);
    const newTableOptions = { ...tableOptions, ...update };
    setComponentsOptions(uID, 'kennzahlenTable', newTableOptions);
  };

  const chartLoading = () => {
    return (
      <div
        style={{
          overflow: 'hidden',
          borderBottomLeftRadius: 5,
          borderBottomRightRadius: 5,
        }}>
        <div className={Loader.loaderwrapper}>
          {!firstValueExpressionKey && (
            <EmptyKpiSelectionInfo
              arrowPosition={[80, 20]}
              details={
                'Daraufhin wird die gewählte Kennzahl sowohl in der Grafik als auch in\n' +
                'der Tabelle unten dargestellt. Es besteht die Möglichkeit, eine zweite\n' +
                'Kennzahl auszuwählen, um beide Kennzahlen miteinander zu vergleichen.'
              }
            />
          )}
          {firstValueExpressionKey && (
            <React.Fragment>
              <div>
                <img src={LoadingChart} alt='' />
              </div>
              <div className={Loader.textLoading}>Daten werden geladen...</div>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  };

  const _chartData = (chartData: { datasets: DatasetType[] }, ve: ValueExpression) => {
    const chartOptions = _getChartOptions((idx: number) => {
      setActiveTooltipIndex(idx);
    });

    const showLegend = ve?.hasChildren();

    const legend = (
      <div className='col-md-4'>
        <div>
          <DetailedChartLegend
            datasets={chartData.datasets}
            onLegendItemToggleAll={_onLegendItemToggleAll}
            onLegendItemClick={_onLegendItemClick}
          />
        </div>
      </div>
    );

    return (
      <Row>
        <div
          className={classNames({
            'col-md-12': !showLegend,
            'col-md-8': showLegend,
          })}
          style={{ position: 'relative' }}>
          <DetailedChartTooltip
            target={chartRef.current}
            model={tooltipModel}
            activeTooltipIndex={activeTooltipIndex}
            position={tooltipPosition}
            xOffset={10}
            compareFallbackColor={'rgba(178, 181, 186, 1)'}
          />
          <div style={{ position: 'relative' }} className={Styles.ChartContainer}>
            <Bar ref={chartRef} data={chartData} options={chartOptions} />
          </div>
        </div>
        {showLegend && legend}
      </Row>
    );
  };

  const chartData = _getChartData();

  return (
    <div className={Styles.DetailedChart} style={{ ...style }}>
      {showDateSelector && (
        <div className={Styles.Header}>
          <div className={Styles.KpiSelectorContainer}>
            <KpiSelector
              onSelect={_onPrimaryValueExpressionSelect}
              selectedValueExpressionKeys={[firstValueExpressionKey, secondValueExpressionKey]}
              placement='bottom-start'
            />
            <Button
              size='sm'
              className={Styles.Btn}
              color='primary'
              onClick={_swap}
              disabled={!secondValueExpressionKey}>
              <MdCompareArrows size={24} />
            </Button>
            <InputGroup>
              <KpiSelector
                onSelect={_onCompareValueExpressionSelect}
                selectedValueExpressionKeys={[secondValueExpressionKey, firstValueExpressionKey]}
                dotColor='#b2b5ba'
                placement='bottom-start'
              />
              {secondValueExpressionKey && (
                <Button color='link' size='sm' outline className={Styles.KpiRemove} onClick={_removeCompare}>
                  <MdHighlightOff size={20} />
                </Button>
              )}
            </InputGroup>
          </div>
          <div>
            <DateSelect onDurationSelected={_onDurationSelected} selectedDuration={duration} />
          </div>
        </div>
      )}
      {chartData && (
        <div className={Styles.ChartKpis}>
          <div>
            <DetailedChartKpiValue
              orgKey={orgKey}
              duration={duration}
              valueExpressionKey={firstValueExpression.identifier}
              selected
              customLabel={firstValueExpression.getLabel()}
              showDetails={false}
              showDot
              customColor='#0091cd'
              rawData={{
                primary: firstKpiPrimaryData?.summaryData?.get(firstValueExpression.identifier),
                compare: firstKpiCompareValue,
              }}
            />
            {secondValueExpression && secondValueExpressionKey && (
              <DetailedChartKpiValue
                orgKey={orgKey}
                duration={duration}
                valueExpressionKey={secondValueExpressionKey}
                customLabel={secondValueExpression.getLabel()}
                customColor='#b2b5ba'
                showDot
                showDetails={false}
                rawData={{
                  primary: secondKpiPrimaryData?.summaryData?.get(secondValueExpression.identifier),
                  compare: secondKpiCompareValue,
                }}
              />
            )}
          </div>
        </div>
      )}
      {chartData ? _chartData(chartData, firstValueExpression) : chartLoading()}
    </div>
  );
};

export default KpiCompareChart;
